import React, { useEffect, useState } from 'react';
import styled from "styled-components";
import Colours from '@shared/ui/atoms/Colours';
import moment, { Moment } from "moment";
import { OfficeEntity } from '@features/desks/services/AdvanceHotDeskingService';
import { getMonthlyOfficeUsage } from '@features/reporting/services/ReportingService';
import { CapacityResponse } from '@features/desks/services/OfficeCapacityService';
import { LoadingSpinner } from '@shared/ui/atoms/LoadingSpinner';
import { Center } from '@shared/ui/atoms/StructuralLayout';
import { OfficeUsageLineChart } from "../components/OfficeUsageLineChart";
import { MountedContentCard, MountedContentCardContent } from '@shared/ui/atoms/MountedContentCard';
import { BodyLarge, BodyRegular } from '@shared/ui/atoms/fonts/Body';
import { useTranslation } from "react-i18next";
import Layout from '@shared/ui/organisms/layout/Layout';
import { OfficeUsageHeader } from "./OfficeUsageHeader";
import { DATE_FORMAT } from "@shared/utils/DateUtils";
import { OfficeUsageContentWrapper } from "./OfficeUsageStructure";
import { OfficeFloorOccupancy } from "./OfficeFloorOccupancy";
import { TeamEntity } from '@shared/models/team.models';
import { Tag } from '@shared/models/tags.models';
import { Department } from "../../departments/data/departments.models";
import { OfficeDeskUsage } from "./OfficeDeskUsage";

export function OfficeUsageReportingPage(props: Props) {
  const [fromDate, setFromDate] = useState<Moment>(moment().startOf('month'));
  const [toDate, setToDate] = useState<any>();
  const [office, setOffice] = useState<OfficeEntity | undefined>();
  const [officeFloor, setOfficeFloor] = useState<OfficeEntity | undefined>();
  const [team, setTeam] = useState<TeamEntity | undefined>();
  const [workGroup, setWorkGroup] = useState<Tag | undefined>();
  const [department, setDepartment] = useState<Department | undefined>();
  const [reportData, setReportData] = useState<any>([]);
  const [loading, setLoading] = useState(false);
  const {t} = useTranslation();

  const onDateChange = (fromDate: Moment) => {
    setFromDate(fromDate.clone());
  }

  const onToDateChange = (date?: Moment) => {
    if (date) {
      setToDate(date.clone());
    }
  }

  const onOfficeSelected = (office: OfficeEntity) => {
    setOffice(office);
  }

  const onOfficeFloorSelected = (officeFloor: OfficeEntity) => {
    setOfficeFloor(officeFloor);
  }

  useEffect(() => {
    if (fromDate && office) {
      setReportData([]);
      setLoading(true);
      const toDateWithFallback = !!toDate ? toDate : fromDate.clone().endOf('month');
      const officeToReportOn = !!officeFloor ? officeFloor : office;
      getMonthlyOfficeUsage(fromDate.clone(), officeToReportOn, toDateWithFallback.clone(), team, workGroup, department).then(resp => {
        const data = resp.map((resp: CapacityResponse) => ({
          name: moment(resp.date).format(DATE_FORMAT),
          capacity: resp.capacity,
          usage: Math.round((resp.amUsed + resp.pmUsed) / 2),
          amUsage: resp.amUsed,
          pmUsage: resp.pmUsed,
          totalUsage: resp.amUsed + resp.pmUsed,
          carParkingCapacity: resp.carParkingCapacity,
          amCarParkingUsage: resp.carParkingUsedAm,
          pmCarParkingUsage: resp.carParkingUsedPm,
          toolTipTitle: moment(resp.date).format('ddd Do MMMM'),
        }));
        setReportData(data);
      })
      .finally(() => setLoading(false));

    }
  }, [department, fromDate, office, officeFloor, team, toDate, workGroup]);

  const onGroupSelected = (selectedTeam?: TeamEntity, selectedWorkGroup?: Tag, selectedDepartment?: Department) => {
    setTeam(selectedTeam);
    setWorkGroup(selectedWorkGroup);
    setDepartment(selectedDepartment);
  }

  return (
    <Layout fullWidth={true} hideRibbon={true}>
      <OfficeUsageHeader setRootOfficeSelected={onOfficeSelected}
                         setOfficeFloorSelected={onOfficeFloorSelected}
                         setDate={onDateChange}
                         setToDate={onToDateChange}
                         loading={loading}
                         fromDate={fromDate.clone()}
                         toDate={toDate?.clone()}
                         selectedRootOffice={office}
                         selectedTeam={team}
                         selectedWorkGroup={workGroup}
                         selectedDepartment={department}
                         onGroupSelected={onGroupSelected} />
      <ReportingContainer>
        {loading ? <>
          <Center>
            <LoadingSpinner hideBorder={true} />
          </Center>
        </> : <>
          {reportData.length === 0 ? <>
            <Center style={{marginTop: 32}}>
              <MountedContentCard>
                <MountedContentCardContent>
                  <BodyLarge weight={600} colour={Colours.blue} style={{marginBottom: 12}}>{t('reports-page.no-data-found')}</BodyLarge>
                  <BodyRegular>{t('reports-page.select-office-from-past-two-years')}</BodyRegular>
                </MountedContentCardContent>
              </MountedContentCard>
            </Center>
          </> : <>
            <OfficeUsageContentWrapper>
              <OfficeUsageLineChart reportData={reportData}
                                    selectedOffice={office}
                                    fromDate={fromDate} />
            </OfficeUsageContentWrapper>
          </>}
        </>}

        <OfficeFloorOccupancy selectedOffice={office} fromDate={fromDate} toDate={toDate} />

        <OfficeDeskUsage selectedOffice={!!officeFloor ? officeFloor : office} fromDate={fromDate} toDate={toDate} />

      </ReportingContainer>
    </Layout>
  );
}

const ReportingContainer = styled.div`
  max-width: 100%;
  min-height: 100vh;
  background-color: white;
  padding: 24px 0;
  .recharts-wrapper .recharts-cartesian-grid-horizontal line {
    stroke-opacity: 0;
  }
  .recharts-cartesian-axis-tick-line {
    display: none !important;
  }
`

interface Props {
}

